/**
 * SectionWebsite — marca, contenido y publicación de la web pública.
 *
 * Es la sección donde el principio de "configurar una vez" se ve más claro
 * (PRODUCT_V2_SPEC §12.1): la web no vuelve a pedir el nombre de la
 * propiedad, las villas, las fotos, los servicios ni las políticas; los toma
 * de lo que ya está configurado en el resto de Ajustes. Por eso el bloque de
 * contenido es una LISTA de lo que ya existe, no otro formulario.
 *
 * "Generar mi web con IA" no genera nada: abre un panel que explica el flujo
 * previsto (§12.3), dice sin rodeos que todavía no está implementado y que
 * cualquier borrador lo revisaría una persona antes de publicarse.
 *
 * Las dos vistas previas son marcadores vacíos a propósito. Una captura
 * simulada daría a entender que la web ya existe, y no existe.
 */
import React, { useState } from "react";
import {
  Browsers, Check, DeviceMobile, Image as ImageIcon, Sparkle,
} from "@phosphor-icons/react";
import Chip from "@/components/ui-dv/Chip";
import SidePanel from "@/components/ui-dv/SidePanel";
import {
  Block, Card, FieldGrid, Field, TextArea, Select, Toggle, Note, Advanced,
} from "@/pages/admin/pms/settings/SettingsPrimitives";
import { useSettingsV2, updateSection, unitLabelFor } from "@/lib/settingsV2Store";
import {
  WEBSITE_STYLES, WEBSITE_TONES, WEBSITE_CONTENT_SOURCES, WEBSITE_AI_STEPS,
  SETTINGS_SECTIONS,
} from "@/data/settingsV2Demo";

/** Para decir de qué sección de Ajustes viene cada pieza de contenido. */
const SECTION_LABEL = Object.fromEntries(SETTINGS_SECTIONS.map((s) => [s.key, s.label]));

const DOMAIN_STATUS = {
  not_configured: { label: "Sin configurar", tone: "stone" },
  configured: { label: "Configurado", tone: "emerald" },
};

export default function SectionWebsite() {
  const { website, property, unitTypes } = useSettingsV2();
  const unitLabel = unitLabelFor(property);

  /**
   * Dos entradas del listado no son texto fijo: se componen con el catálogo y
   * con el vocabulario elegido en Propiedad. Si el cliente llama "habitación"
   * a su unidad, aquí también lo dice.
   */
  const sourceLabel = (source) => {
    if (source.label) return source.label;
    if (source.key === "unit_types") {
      return `${unitTypes.length} tipos de ${unitLabel.singular.toLowerCase()}`;
    }
    if (source.key === "photos") {
      const total = unitTypes.reduce((n, t) => n + (t.photos_count || 0), 0);
      return `${total} fotos`;
    }
    return source.key;
  };
  const set = (patch) => updateSection("website", patch);
  const [aiOpen, setAiOpen] = useState(false);

  const styleHint = (WEBSITE_STYLES.find((s) => s.value === website.style) || {}).hint;
  const domain = DOMAIN_STATUS[website.domain_status] || DOMAIN_STATUS.not_configured;

  return (
    <div data-testid="section-website">
      {/* -- Marca --------------------------------------------------------- */}
      <Block
        title="Marca"
        description="El aspecto de la web: tu logotipo, tus colores y el tono con el que hablas."
        testId="website-brand"
      >
        <Note testId="website-brand-note">
          En la vista previa las imágenes son marcadores de posición: no se sube ningún archivo.
        </Note>

        <Card>
          <BrandSlot
            label="Logotipo"
            hint="Es el de Propiedad: se sube una vez y lo usan web, portal y documentos."
            filled={property.logo_set}
            onToggle={() => updateSection("property", { logo_set: !property.logo_set })}
            testId="website-logo"
          />

          <div className="mt-5 pt-5 border-t border-stone-100 dark:border-stone-800">
            <FieldGrid>
              <ColorField
                label="Color principal"
                value={website.brand_primary}
                onChange={(v) => set({ brand_primary: v })}
                hint="Botones y acentos"
                testId="website-brand-primary"
              />
              <ColorField
                label="Color del texto"
                value={website.brand_ink}
                onChange={(v) => set({ brand_ink: v })}
                hint="Títulos y párrafos"
                testId="website-brand-ink"
              />

              <div>
                <Select
                  label="Estilo visual"
                  value={website.style}
                  onChange={(v) => set({ style: v })}
                  options={WEBSITE_STYLES.map((s) => ({ value: s.value, label: s.label }))}
                  testId="website-style"
                />
                {styleHint && (
                  <p
                    className="mt-1.5 text-xs text-stone-500 dark:text-stone-400"
                    data-testid="website-style-hint"
                  >
                    {styleHint}
                  </p>
                )}
              </div>

              <Select
                label="Tono de los textos"
                value={website.tone}
                onChange={(v) => set({ tone: v })}
                options={WEBSITE_TONES.map((t) => ({ value: t.value, label: t.label }))}
                hint="Cómo suena la web"
                testId="website-tone"
              />
            </FieldGrid>
          </div>

          <div className="mt-5 pt-5 border-t border-stone-100 dark:border-stone-800">
            <BrandSlot
              label="Imagen principal"
              hint="La foto grande de la portada."
              filled={website.hero_set}
              onToggle={() => set({ hero_set: !website.hero_set })}
              testId="website-hero"
              tall
            />
          </div>
        </Card>
      </Block>

      {/* -- Contenido ----------------------------------------------------- */}
      <Block
        title="De dónde sale el contenido"
        description="La web se construye con lo que ya has configurado."
        testId="website-content"
      >
        <Note testId="website-content-note">
          Nada de esto hay que escribirlo dos veces. Si cambias una foto, un precio o una
          política en su sección, la web lo recoge en la siguiente publicación.
        </Note>

        <Card padded={false} testId="website-content-sources">
          <ul className="divide-y divide-stone-100 dark:divide-stone-800">
            {WEBSITE_CONTENT_SOURCES.map((source) => (
              <li
                key={source.key}
                className="flex items-center gap-3 px-4 py-3 min-h-[52px]"
                data-testid={`website-source-${source.key}`}
              >
                <span className="shrink-0 w-5 h-5 rounded-full bg-stone-100 dark:bg-stone-800 flex items-center justify-center text-stone-500 dark:text-stone-300">
                  <Check size={12} weight="bold" />
                </span>
                <span className="min-w-0 flex-1 text-sm text-stone-900 dark:text-stone-100">
                  {sourceLabel(source)}
                </span>
                <span className="shrink-0 text-[11px] text-stone-400 dark:text-stone-500">
                  Ya en {SECTION_LABEL[source.section] || "Ajustes"}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </Block>

      {/* -- Generación ---------------------------------------------------- */}
      <Block
        title="Generar la web"
        description="Un primer borrador con tus datos, para no empezar desde una página en blanco."
        testId="website-generate-block"
      >
        <Note testId="website-generate-note">
          Todavía no está implementado. Al pulsar solo se abre la explicación del flujo
          previsto: no se genera ninguna web y no se publica nada.
        </Note>

        <Card>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-sm text-stone-600 dark:text-stone-300 max-w-xl">
              La IA propondría la estructura y los textos a partir de la información que ya
              tienes configurada. Tú los lees, los cambias y decides si se publican.
            </p>
            <button
              type="button"
              onClick={() => setAiOpen(true)}
              data-testid="website-generate"
              className="dv-btn dv-btn-primary text-sm min-h-[44px] inline-flex items-center justify-center gap-2 shrink-0"
            >
              <Sparkle size={16} weight="fill" />
              Generar mi web con IA
            </button>
          </div>
        </Card>
      </Block>

      {/* -- Publicación --------------------------------------------------- */}
      <Block
        title="Publicación"
        description="Dónde vive la web y si acepta reservas directas."
        testId="website-publish"
      >
        <Card>
          <FieldGrid>
            <div>
              <Field
                label="Dominio"
                value={website.domain}
                onChange={(v) => set({ domain: v })}
                placeholder="tupropiedad.com"
                testId="website-domain"
              />
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <Chip tone={domain.tone} size="xs" dot data-testid="website-domain-status">
                  {domain.label}
                </Chip>
                <span className="text-[11px] text-stone-400 dark:text-stone-500">
                  Se conecta el día que decidas publicar
                </span>
              </div>
            </div>
          </FieldGrid>

          <div className="mt-5 pt-4 border-t border-stone-100 dark:border-stone-800">
            <Toggle
              label="Reserva directa desde la web"
              description="El huésped reserva y paga en tu web, sin comisión de portal."
              checked={website.direct_booking_enabled}
              onChange={(v) => set({ direct_booking_enabled: v })}
              testId="website-direct-booking"
            />
            <div className="mt-2">
              <Note testId="website-direct-booking-note">
                Para que funcione de verdad hacen falta dos cosas que aún no están montadas:
                la disponibilidad en tiempo real y una forma de cobrar. Hasta entonces la web
                puede ofrecer un formulario de consulta o un botón de WhatsApp.
              </Note>
            </div>
          </div>

          <Advanced label="Texto para buscadores" testId="website-seo">
            <FieldGrid>
              <Field
                label="Título"
                value={website.seo_title}
                onChange={(v) => set({ seo_title: v })}
                hint="Lo que se lee en el buscador"
                span={2}
                testId="website-seo-title"
              />
              <TextArea
                label="Descripción"
                value={website.seo_description}
                onChange={(v) => set({ seo_description: v })}
                rows={2}
                testId="website-seo-description"
              />
            </FieldGrid>
          </Advanced>
        </Card>
      </Block>

      {/* -- Vista previa -------------------------------------------------- */}
      <Block
        title="Vista previa"
        description="Antes de publicar podrás ver la web en pantalla grande y en el móvil."
        testId="website-preview"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PreviewSlot
            label="Escritorio"
            icon={Browsers}
            caption="Aquí aparecerá la vista de escritorio cuando exista un borrador."
            testId="website-preview-desktop"
          />
          <PreviewSlot
            label="Móvil"
            icon={DeviceMobile}
            caption="Aquí aparecerá la vista de móvil cuando exista un borrador."
            testId="website-preview-mobile"
            narrow
          />
        </div>
      </Block>

      {/* -- Panel del flujo previsto -------------------------------------- */}
      <SidePanel
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        title="Generar la web con IA"
        subtitle="Flujo previsto"
        testId="website-ai-panel"
        footer={
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => setAiOpen(false)}
              data-testid="website-ai-dismiss"
              className="dv-btn dv-btn-ghost text-sm min-h-[44px]"
            >
              Cerrar
            </button>
          </div>
        }
      >
        <p className="text-sm text-stone-600">
          Así funcionaría el día que exista, paso a paso.
        </p>

        <ol className="space-y-4" data-testid="website-ai-steps">
          {WEBSITE_AI_STEPS.map((step, index) => (
            <li
              key={step.key}
              className="flex items-start gap-3"
              data-testid={`website-ai-step-${step.key}`}
            >
              <span className="shrink-0 w-7 h-7 rounded-full border border-stone-200 flex items-center justify-center text-xs tabular-nums text-stone-500">
                {index + 1}
              </span>
              <span className="min-w-0">
                <span className="block text-sm text-stone-800">{step.label}</span>
                <span className="block text-xs text-stone-500 mt-0.5">{step.detail}</span>
              </span>
            </li>
          ))}
        </ol>

        <div
          className="pt-4 border-t border-stone-100 dark:border-stone-800 space-y-2"
          data-testid="website-ai-disclaimer"
        >
          <p className="text-xs text-stone-500">
            Esta generación todavía no está implementada. Pulsar el botón no crea ninguna web,
            no escribe ningún texto y no publica nada.
          </p>
          <p className="text-xs text-stone-500">
            Cuando exista, lo que proponga la IA será siempre un borrador: una persona lo
            revisa y lo aprueba antes de que se publique.
          </p>
        </div>
      </SidePanel>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Piezas locales de esta sección
 * ---------------------------------------------------------------------- */

/** Campo de color con su muestra al lado, para no tener que imaginarse el tono. */
function ColorField({ label, value, onChange, hint, testId }) {
  return (
    <div>
      <div className="flex items-end gap-3">
        <div className="flex-1 min-w-0">
          <Field
            label={label}
            value={value}
            onChange={onChange}
            hint={hint}
            placeholder="#9C5236"
            testId={testId}
          />
        </div>
        <span
          aria-hidden="true"
          data-testid={`${testId}-swatch`}
          className="shrink-0 w-10 h-10 rounded-lg border border-stone-200 dark:border-stone-700"
          style={{ backgroundColor: value || "transparent" }}
        />
      </div>
    </div>
  );
}

/** Marcador de imagen: en la vista previa solo cambia de estado, no sube nada. */
function BrandSlot({ label, hint, filled, onToggle, testId, tall }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testId}
      aria-pressed={filled}
      className={`w-full rounded-xl border border-dashed flex flex-col items-center justify-center gap-2 px-4 transition-colors ${
        tall ? "min-h-[132px]" : "min-h-[108px]"
      } ${
        filled
          ? "border-stone-300 bg-stone-50 dark:border-stone-700 dark:bg-stone-900"
          : "border-stone-300 dark:border-stone-700 hover:border-stone-400 dark:hover:border-stone-600"
      }`}
    >
      <ImageIcon size={20} className="text-stone-400" weight={filled ? "fill" : "regular"} />
      <span className="text-sm text-stone-700 dark:text-stone-300">{label}</span>
      {hint && (
        <span className="text-[11px] text-stone-400 dark:text-stone-500 text-center">{hint}</span>
      )}
      <span className="text-[11px] text-stone-400 dark:text-stone-500">
        {filled ? "Configurado · pulsa para vaciar" : "Sin configurar · pulsa para simular"}
      </span>
    </button>
  );
}

/** Hueco de vista previa. Vacío a conciencia: no hay web que enseñar todavía. */
function PreviewSlot({ label, icon: Icon, caption, testId, narrow }) {
  return (
    <div className="dv-card" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-stone-400" />
        <span className="text-[11px] tracking-[0.18em] uppercase text-stone-500 dark:text-stone-400">
          {label}
        </span>
      </div>
      <div
        className={`rounded-lg border border-dashed border-stone-300 dark:border-stone-700 bg-stone-50/60 dark:bg-stone-900/40 min-h-[180px] flex items-center justify-center px-6 ${
          narrow ? "max-w-[260px] mx-auto" : ""
        }`}
      >
        <p className="text-xs text-stone-400 dark:text-stone-500 text-center max-w-[220px]">
          {caption}
        </p>
      </div>
    </div>
  );
}
